import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import {
  TPaymentFiltersForm,
  TPayoutsFiltersForm,
} from '../../forms/PaymentsFilters/types';
import { isNil, omitBy } from 'lodash';
import { TCurrency, TTransactionStatus } from '../../../../../api/src';
import {
  getArrayOrUndefined,
  getFloatOrUndefined,
} from '../../helpers/url-params';
import { PAYMENT_FILTERS_CREDIT_STATUSES } from '../../const';
import { useURLContractCurrencies } from '../use-url-contract-currencies';
import { usePagerPropsFromUrl } from '../pager/use-pager-props-from-url';
import { TPayoutsURLParams } from './types';
import { useSelectedContract } from '../contracts';
import dayjs from 'dayjs';

const formValuesToParams = (
  values: TPayoutsFiltersForm,
  isBulkPayoutAllowed?: boolean,
): TPayoutsURLParams => {
  const endDate = values.EndDate && values.EndDate.trim() !== '' 
    ? values.EndDate 
    : dayjs().format('DD.MM.YYYY');
  
  const tmp: TPayoutsURLParams = {
    CurrencyCodes: values.CurrencyCodes ?? undefined,
    StartDate: values.StartDate ? values.StartDate : undefined,
    EndDate: endDate,
    StartAmount: values.StartAmount?.toString() ?? undefined,
    EndAmount: values.EndAmount?.toString() ?? undefined,
    TransactionStatuses: values.TransactionStatuses ?? undefined,
    OrderId: values.OrderId ?? undefined,
    ...(isBulkPayoutAllowed
      ? { PayoutsType: values.PayoutsType ?? 'All' }
      : {}),
    IncludeBulkPayouts: values.IncludeBulkPayouts?.toString() || undefined,
    CardLast4: values.CardLast4 ?? undefined,
    Userdata: values.Userdata ?? undefined,
    RRN: values.RRN ?? undefined,
  };
  return omitBy(tmp, isNil);
};

const queryToFilters = (params: URLSearchParams, currencies: TCurrency[]) => {
  return {
    CurrencyCodes: getArrayOrUndefined<TCurrency>(
      params.getAll('CurrencyCodes'),
      currencies,
    ),
    StartDate: params.get('StartDate') ?? '',
    EndDate: params.get('EndDate') ?? dayjs().format('DD.MM.YYYY'),
    StartAmount: getFloatOrUndefined(params.get('StartAmount')),
    EndAmount: getFloatOrUndefined(params.get('EndAmount')),
    TransactionStatuses: getArrayOrUndefined<TTransactionStatus>(
      params.getAll('TransactionStatuses'),
      PAYMENT_FILTERS_CREDIT_STATUSES,
    ),
    OrderId: params.get('OrderId') ?? params.get('SearchQuery'),
    PayoutsType: params.get('PayoutsType') ?? 'All',
    IncludeBulkPayouts: params.get('IncludeBulkPayouts'),
    CardLast4: params.get('CardLast4'),
    Userdata: params.get('Userdata'),
    RRN: params.get('RRN'),
  } as TPayoutsFiltersForm;
};

export const useSetPayoutsUrlSearchParams = () => {
  const [, setParams] = useSearchParams();
  const { PageSize } = usePagerPropsFromUrl();
  const { isBulkPayoutAllowed } = useSelectedContract() || {};

  return useCallback(
    (values: TPaymentFiltersForm) => {
      const params = formValuesToParams(values, isBulkPayoutAllowed ?? undefined);
      if (PageSize !== 10) {
        params.PageSize = PageSize.toString();
      }
      setParams(params);
    },
    [PageSize, setParams, isBulkPayoutAllowed],
  );
};

export const usePayoutsFiltersFromUrl = () => {
  const [params] = useSearchParams();
  const currencies = useURLContractCurrencies();
  return useMemo(
    () => queryToFilters(params, currencies),
    [currencies, params],
  );
};
